<template>
  <b-modal
      v-model="showModal"
      size="lg"
      id="unitCategories-modal"
      :title="edited.id > 0 ? 'Edit ' + (companyPurpose !== 'rent' ? 'product' : 'unit') + ' category details' : 'Add new ' + (companyPurpose !== 'rent' ? 'product' : 'unit') + ' category' "
      :ok-title="isFormLoading ? 'Saving...' : 'Save'"
      :ok-disabled="isFormLoading"
      cancel-variant="outline-secondary"
      @ok="onSubmit($event)"
  >

    <div v-if="isContentLoading" class="text-center pt-3">
      <b-spinner label="Loading..." />
    </div>
    <b-form v-else>
      <b-row>
        <b-col cols="12">
          <b-form-group
              label="Name"
              label-for="name"
          >
            <b-form-input
                v-model="edited.name"
                placeholder="Name"
            />
            <small class="text-danger" v-if="v$.edited?.name.$error">Name is required</small>
          </b-form-group>
          <b-form-group
              label="Description"
              label-for="description"
          >
            <b-form-textarea
                v-model="edited.description"
                placeholder="Description"
                rows="3"
                max-rows="6"
            />

          </b-form-group>
          <b-form-group
              label="Parent category"
              label-for="parentCategoryId"
          >
            <b-form-select
                id="parentCategoryId"
                v-model="edited.parent_category_id"
            >
              <b-form-select-option :value="null">None</b-form-select-option>
              <template v-for="category in unitCategories" :key="category.id">
                <b-form-select-option
                    v-if="category.id !== edited.id"
                    :value="category.id"
                >
                  {{ category.name }}
                </b-form-select-option>
              </template>
            </b-form-select>
          </b-form-group>

          <b-form-group
              label="Picture"
              label-for="picture"
          >
            <b-form-file
                v-model="edited.picture"
                placeholder="Choose a file or drop it here..."
                drop-placeholder="Drop file here..."
            />
            <img v-if="edited.picture_preview ?? false"
                 :src="edited.picture_preview.includes('https') ? edited.picture_preview : '//' + edited.picture_preview"
                 :style="{ width: '150px', border: '1px solid #DDDDDD' }" class="my-1"
            />
          </b-form-group>

        </b-col>
      </b-row>

    </b-form>

  </b-modal>
</template>
<script setup>
import { watch, onMounted, ref, computed, defineComponent, reactive, inject, getCurrentInstance } from 'vue';
import {mapActions, mapGetters, useStore} from "vuex";
import vSelect from 'vue-select';
import {
  BButton, BCol, BDropdownItem,
  BForm,
  BFormCheckbox,
  BFormDatepicker, BFormFile,
  BFormGroup,
  BFormInput, BFormSelect, BFormSelectOption,
  BFormTextarea, BModal, BRow,
  BSpinner,
  VBModal
} from "bootstrap-vue";
import useVuelidate from '@vuelidate/core';
import {email, integer, minLength, minValue, numeric, required} from "@vuelidate/validators";
import {sweetError, sweetWarning} from "@/libs/sweet-alerts";
//import PhoneMaskInput from 'vue-phone-mask-input';
import { toastSuccess, toastFailure } from '@/libs/toastification';

const cloneObject = (object) => {
  return JSON.parse(JSON.stringify(object))
}

const companyPurpose = ref(window.companyPurpose);
const companyId = ref(window.companyId);
const minDate = new Date();

const defaultUnitCategory = {
  id: null,
  name: null,
  description: null,
  picture: null,
  picture_preview: null,
  parent_category_id: null,
};

const props = defineProps({
  unitCategories: {
    type: Object,
    required: true
  },
  unitCategory: {
    type: Object,
    required: true
  },
  clickDate: {
    type: Date,
    required: true
  }
});

const showModal = ref(false);
const isContentLoading = ref(true);
const isFormLoading = ref(false);

const unitCategory = computed(() => store.state.unitCategories.unitCategory);

let temporaryPicture = null;
let edited = reactive(cloneObject(defaultUnitCategory));

const rules = {
  edited: {
    name: { required, minLength: minLength(2) },
  },
};
const v$ = useVuelidate(rules, { edited });

const store = useStore();

watch(unitCategory, (newUnitCategory) => {
  if (newUnitCategory?.data && newUnitCategory.data.id > 0) {

    edited.id = newUnitCategory.data.id;
    edited.name = newUnitCategory.data.name;
    edited.description = newUnitCategory.data.description;
    edited.picture_preview = newUnitCategory.data.picture;
    edited.parent_category_id = newUnitCategory.data.parent_category_id;
  } else {
    Object.assign(edited, cloneObject(defaultUnitCategory));
  }
});

watch(
    () => props.clickDate,
    (newVal) => {

      if (props.unitCategory !== null ) {

        const unitCategoryId = props.unitCategory && props.unitCategory.id;
        store.dispatch('unitCategories/loadUnitCategory', unitCategoryId)
            .then(() => {
              isContentLoading.value = false;
              showModal.value = true;
            })
            .catch((error) => {
              isContentLoading.value = false;
              showModal.value = false;
              toastFailure('Something went wrong: ' + error.message);
            });
      } else {

        Object.assign(edited, cloneObject(defaultUnitCategory));
        isContentLoading.value = false;
        showModal.value = true;
      }
    }
);


const onSubmit = async (event) => {
  if (isFormLoading.value) {
    return;
  }

  event.preventDefault();
  isFormLoading.value = true;
  const isFormCorrect = await v$.value.edited.$validate();
  if (!isFormCorrect) {
    isFormLoading.value = false;
    return;
  }

  await submitForm();
};

const getTemporaryPicture = async () => {
  return new Promise(resolve => {
    const reader = new FileReader()

    reader.addEventListener('load', () => {
      temporaryPicture = reader.result
      resolve()
    }, false)

    if (/\.(jpe?g|png|gif)$/i.test(edited.picture.name)) {
      reader.readAsDataURL(edited.picture)
    }
  })
}

const submitForm = async () => {
  const formData = new FormData();

  for (const [key, value] of Object.entries(edited)) {
    if (value) {
      formData.append(key, value)
    }
  }

  if (edited.picture) {
    await getTemporaryPicture();
  }


  if (edited.id != null) {

    formData.append('_method', 'PUT');

    store.dispatch('unitCategories/updateUnitCategory', {formData})
        .then(() => {
          showModal.value = false;
          toastSuccess('Successfully updated');
        })
        .catch((error) => {
          toastFailure('Failed to update');
        })
        .finally(() => {
          isFormLoading.value = false;
        });
  } else {

    formData.append('_method', 'POST');
    store.dispatch('unitCategories/addUnitCategory', {formData})
        .then(() => {
          showModal.value = false;
          toastSuccess('Successfully added');
        })
        .catch((error) => {
          console.log('error:', error);
          toastFailure('Failed to add');
        })
        .finally(() => {
          isFormLoading.value = false;
        });
  }

  edited.picture = null;
  temporaryPicture = null;

};

</script>